import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { OrganizationKeyName } from "../../model/Organization";
import { RootState } from "../../store";
import Spinner from "../common/Spinner";

import * as userActions from "./userActions";
import * as userSelectors from "./userSelectors";
import * as authSelectors from "./../auth/authSelectors";
import UserListRow from "./UserListRow";
import UserListFiltersForm from "./UserListFiltersForm";
import { GetUserListSubtype } from "./userActions";
import * as organizationSelectors from "../organization/organizationSelectors";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { PrimaryButton } from "../../components/Buttons";
import { CardHeader } from "../../components/CardHeader";

interface Props {}

const UserList: React.FC<Props> = () => {
  // selectors
  const users = useSelector(userSelectors.userList);
  const hasMore = useSelector(userSelectors.hasNextPage);
  const userListFilters = useSelector(userSelectors.filters);
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const isLoadingUsers = useSelector(userSelectors.isLoadingUsers);
  const isNameColVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListColumnVisible(state, "name")
  );
  const isEmailColVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListColumnVisible(state, "email")
  );
  const isJoinDateColVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListColumnVisible(state, "joinDate")
  );
  const isSignupCountryColVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListColumnVisible(state, "signupCountry")
  );
  const isStatusColVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListColumnVisible(state, "status")
  );
  const isStatusFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListFilterVisible(state, "status")
  );
  const isCountryFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListFilterVisible(state, "country")
  );
  const isNameOrEmailFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListFilterVisible(state, "nameOrEmail")
  );
  const isPhoneNbFilterVisible = useSelector((state: RootState) =>
    organizationSelectors.isUserListFilterVisible(state, "phoneNb")
  );
  // dispatch
  const dispatch = useDispatch();
  const getMoreUsers = (orgKeyName: OrganizationKeyName) =>
    dispatch(
      userActions.getUserListAction(GetUserListSubtype.LoadNextPage, orgKeyName)
    );
  const setFiltersAndSearch = (
    organization: OrganizationKeyName,
    values: any
  ) => dispatch(userActions.setFiltersUserListAction(organization, values));
  const resetFilters = (organization: OrganizationKeyName) =>
    dispatch(userActions.resetFiltersUserListAction(organization));

  const [pickedUsers, setPickedUsers] = React.useState(new Set());

  // fn
  const onLoadMoreBtnClick = () => {
    getMoreUsers(organizationKeyName);
  };

  const onSearch = (values: any) => {
    setFiltersAndSearch(organizationKeyName, values);
    setPickedUsers(new Set());
  };

  const onReset = () => {
    resetFilters(organizationKeyName);
    setPickedUsers(new Set());
  };

  const onUserToggle = (userId: number) => {
    const exists = pickedUsers.has(userId);
    const newSelectedUsers = new Set(pickedUsers);
    if (exists) {
      newSelectedUsers.delete(userId);
    } else {
      newSelectedUsers.add(userId);
    }
    setPickedUsers(newSelectedUsers);
  };

  // hooks
  React.useEffect(() => {
    dispatch(
      userActions.getUserListAction(
        GetUserListSubtype.LoadFirstPage,
        organizationKeyName
      )
    );
  }, [dispatch, organizationKeyName]);

  return (
    <div className="container mt-7">
      <div className="row row-cards row-deck">
        <div className="col-12">
          <UserListFiltersForm
            onSubmit={onSearch}
            onReset={onReset}
            currentFilters={userListFilters}
            isCountryFilterVisible={isCountryFilterVisible}
            isNameOrEmailFilterVisible={isNameOrEmailFilterVisible}
            isPhoneNbFilterVisible={isPhoneNbFilterVisible}
            isStatusFilterVisible={isStatusFilterVisible}
          />
        </div>
      </div>

      <div className="card">
        <CardHeader title="User list" />
        <div className="card-body d-flex flex-column pb-0">
          <Table>
            <thead>
              <TableRowHeader>
                {isNameColVisible && (
                  <TableCellHeaderText className="pl-3">
                    Name
                  </TableCellHeaderText>
                )}
                {isEmailColVisible && (
                  <TableCellHeaderText>Email</TableCellHeaderText>
                )}
                {isStatusColVisible && (
                  <TableCellHeaderText>Status</TableCellHeaderText>
                )}
                {isJoinDateColVisible && (
                  <TableCellHeaderText>Join date</TableCellHeaderText>
                )}
                {isSignupCountryColVisible && (
                  <TableCellHeaderText>Signup country</TableCellHeaderText>
                )}
                <th />
              </TableRowHeader>
            </thead>
            <tbody>
              {users?.map((user, index) => {
                return (
                  <UserListRow
                    key={index}
                    user={user}
                    isNameColVisible={isNameColVisible}
                    isEmailColVisible={isEmailColVisible}
                    isJoinDateColVisible={isJoinDateColVisible}
                    isSignupCountryColVisible={isSignupCountryColVisible}
                    isStatusColVisible={isStatusColVisible}
                    onUserToggle={onUserToggle}
                    isUserSelected={false}
                  />
                );
              })}
            </tbody>
          </Table>
        </div>

        <div className="card-footer text-center ml-5 mr-5">
          {isLoadingUsers ? (
            <Spinner />
          ) : (
            <>
              {hasMore ? (
                <PrimaryButton
                  backgroundColor={colors.white}
                  hoverColor={colors.ctaLight}
                  width="100%"
                  color={colors.textPrimary}
                  onClick={() => onLoadMoreBtnClick()}
                >
                  Load more
                </PrimaryButton>
              ) : (
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  No more users
                </StyledText>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserList;
